import * as React from 'react';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AInputSearch } from '../../atoms/filter/InputSearchFilter';
import { useMe } from '../../atoms/global/users';
import { AtomCategory } from '../../atoms/utils/model/Model';
import { getColorFromList } from '../../components_v2/avatar/Data/ColorList';
import ClientCompany from '../../components_v2/clientCompany/ClientCompany';
import { getUserCategories, Group } from '../../components_v2/Selector/actions';
import ToolbarFilter, { ToolbarElement } from '../../components_v2/toolbarFilter/ToolbarFilter';
import { translateToString } from '../../styles/global/translate';
import { TitleAndChild } from '../client-companies/popup/ColumnOrganizer';
import { InstancePopup } from '../forms/formInstancePopup';
import { ToolbarState } from '../globals/mainPage/mainPage';
import NoData from '../noData/NoData';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import { FlexDiv } from '../products/style';
import { getReports, Report } from './action';
import Generic, { NewReport } from './generic/generic';
import { ReportInterpretorProviderProps } from './interpretor/hoverContext';
import { ModalLeft } from './ModalLeft';
import { ModalLeftToggle } from '../map/modalLeft/ModalLeftFilter';
import { initState, reducer, reportingAction, reportingState } from './Reducer';
import { SurfaceColor } from '../../styles/global/css/Utils';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

export interface SetSelectedReport {
	height: number
	isModalOpen: boolean
	setIsModalOpen: (isOpen: boolean) => void
	state: reportingState
	dispatch: (action: reportingAction) => void
	reports: Report[]
	setUserCategories: (categories: Group[]) => void
}

interface NoReportSelected {
	userCategories: Group[]
	dispatch: (action: reportingAction) => void
	reports: Report[]
	setIsModalOpen: (isOpen: boolean) => void
}

const ReportCard = styled.div<{ colorMarker: string }>`
	height: 85px;
	width: 150px;
	padding: 12px 10px;
	display: flex;
	align-items: center;
	cursor: pointer;
	font-size: 12px;
	background-color: color-mix(in oklab, ${p => p.colorMarker}, transparent 90%);
	color: color-mix(in oklab, ${p => p.colorMarker}, black 40%);
	border-radius: 8px;
	&:hover {
		background-color: color-mix(in oklab, ${p => p.colorMarker}, transparent 80%);
	}
`;

const ReportCardName = styled.div`
	display: -webkit-box;
	-webkit-box-orient: vertical;
	overflow: hidden;
	-webkit-line-clamp: 3;
`;

function ReportSelection(props: NoReportSelected) {
	const inputSearchText = useRecoilValue(AInputSearch);
	const sortedUserCategories = React.useMemo(() => {
		return [...props.userCategories].sort((a, b) =>
			a.name === 'shared_reports' ? 1 :
				b.name === 'shared_reports' ? -1 :
					a.sequence - b.sequence
		);
	}, [props.userCategories]);

	return (
		<div style={{ width: '100%', height: '100%', backgroundColor: SurfaceColor }}>
			<FlexDiv flow='column' align='stretch' padding='20px' overflow='auto' gap='20px' style={{ height: '100%' }}>
				{sortedUserCategories.map((category, _i) => {
					const categoryReports = props.reports.filter(
						(report) => category.inside.find((c) => c.id === report.id && c.type_ === 'Report' && !c.ishidded) !== undefined
					);
					if (categoryReports.length === 0) return null;
					const categoryColor = category.color ?? getColorFromList(category.name);
					const categoryReportsSorted = [...categoryReports].filter((r) => r.name.toLowerCase().includes(inputSearchText.toLowerCase())).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
					if (categoryReportsSorted.length === 0) return null;
					return (
						<TitleAndChild key={category.id} title={category.name === 'shared_reports' ? translateToString('reports.categories.shared_reports') : category.name} colorMarker={categoryColor}>
							<FlexDiv gap='12px' flexWrap='wrap'>
								{categoryReportsSorted.map((r) => (
									<ReportCard
										key={r.id}
										onClick={() => {
											props.dispatch({
												type: 'SET_SELECTED_REPORT',
												value: r.id.toString(),
												data: { ...r },
											});
										}
										}
										colorMarker={categoryColor}
									>
										<ReportCardName title={r.name}>{r.name}</ReportCardName>
									</ReportCard>
								))}
							</FlexDiv>
						</TitleAndChild>
					);
				})}
			</FlexDiv>
		</div>
	);
}


export function Reports(props: {
	updateToolbarState: (value: ToolbarState) => void
	setToolBarState: (value: ToolbarState) => void
}) {
	const me = useMe();
	const history = useHistory();
	const [state, dispatch] = React.useReducer(
		(s: reportingState, a: reportingAction) => reducer(s, a, history),
		initState()
	);
	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(true);
	// The report view replaces the whole left toolbar slot, so its updates get the toggle prepended
	const isModalOpenRef = React.useRef(isModalOpen);
	isModalOpenRef.current = isModalOpen;
	const updateToolbarStateWithToggle = React.useCallback((value: ToolbarState) => props.updateToolbarState({
		...value,
		bottomLeftToolbarComponent: <FlexDiv gap='10px'>
			<ModalLeftToggle isOpen={isModalOpenRef.current} onClick={() => setIsModalOpen(open => !open)} />
			{value.bottomLeftToolbarComponent}
		</FlexDiv>
	}), [props.updateToolbarState]);
	const [companyModal, setCompanyModal] = React.useState<ModalState<number>>({ isOpen: false });
	const [formModalState, setFormModalState] = React.useState<ModalState<string>>({ isOpen: false });
	const [reports, setReports] = React.useState<Report[]>([]);
	const { innerHeight: height } = window;
	const padding = 1;
	const true_height = 98 - 100 / (height / padding);
	const marginLeft = isModalOpen ? 260 : 0;
	const { isAllowedTo } = React.useContext(PermissionContext);
	const [noData, setNoData] = React.useState<boolean>(false);
	const [userCategories, setUserCategories] = React.useState<Group[]>([]);
	const localStateReportId = localStorage.getItem('report_id_to_view');

	const updateGenericReports = async() => {
		const r = await getReports();
		setReports(r);
		if (r.length === 0) {
			setNoData(true);
		}
		else {
			if (!localStateReportId)
				history.push({ hash: 'list' });
		}
	};

	React.useEffect(() => {
		if (isAllowedTo({ objectAction: 'ViewReporting' })) {
			updateGenericReports();
		}
		getUserCategories(me.id).then(data => {
			setUserCategories(data);
		});
	}, []);

	React.useEffect(() => {
		props.setToolBarState({
			title: translateToString('general.reports'),
			bottomLeftToolbarComponent: <FlexDiv gap='10px'>
				<ModalLeftToggle isOpen={isModalOpenRef.current} onClick={() => setIsModalOpen(open => !open)} />
				<ToolbarFilter
					category={AtomCategory.REPORTS}
					elements={[
						ToolbarElement.INPUT_SEARCH
					]}
				/>
			</FlexDiv>
		});
	}, []);

	React.useEffect(() => {
		if (history.location.hash === '#list') {
			localStorage.removeItem('report_id_to_view');
		}
	}, [history.location.hash]);

	React.useEffect(() => {
		if (localStateReportId && !(history.location.hash === '#list')) {
			const report = reports.find(r => r.id.toString() === localStateReportId);
			if (!report) return;
			dispatch({
				type: 'SET_SELECTED_REPORT',
				value: localStateReportId,
				data: { ...report },
			});
		}
	}, [localStateReportId, reports]);

	const reportInterpretorProps: ReportInterpretorProviderProps & { state: reportingState, dispatch: React.Dispatch<reportingAction> } = {
		state,
		dispatch,
		onFormInstanceClick: uuid => setFormModalState({ isOpen: true, data: uuid, fullOpenMode: true }),
		onCompanyClick: data => setCompanyModal({ isOpen: true, data }),
	};

	return (
		<Restricted to={{ objectAction: 'ViewReporting' }}>
			<div>
				<div style={{ overflow: 'hidden', marginTop: padding + 'px' }}>
					<ModalLeft
						height={true_height}
						isModalOpen={isModalOpen}
						setIsModalOpen={setIsModalOpen}
						state={state}
						dispatch={dispatch}
						reports={reports}
						setUserCategories={setUserCategories}
					/>
					<div style={{ marginLeft: `${marginLeft}px`, height: `calc(100vh - ${TOOLBARS_HEIGHT + 2}px)`, transition: '0.2s' }}>
						{history.location.hash === '#list' ?
							<ReportSelection
								userCategories={userCategories}
								dispatch={dispatch}
								reports={reports}
								setIsModalOpen={setIsModalOpen}
							/> :
							state.data && <Generic
								reprasentation={state.data.value}
								setToolBarState={updateToolbarStateWithToggle}
								{...reportInterpretorProps}
								updateGenericReports={updateGenericReports}
							/>
						}
						{noData && <NoData height='100%' message={translateToString('reports.no_report')} messageButton={translateToString('reports.create_report')} onClickButton={() => {
							alert({
								title: translateToString('reports.name_report'),
								content: (onBreak, onOk) => <NewReport onBreak={onBreak} onOk={onOk} />,
								noButtons: true,
							});
						}} />}
					</div>
				</div>
			</div>
			<ClientCompany
				isOpen={companyModal.isOpen}
				setOpen={(isOpen) => setCompanyModal({ isOpen })}
				clientCompanyId={companyModal.data ?? 0}
			/>
			<InstancePopup modalState={formModalState} isOpen={formModalState.isOpen}
				onClickOut={() => setFormModalState({ ...formModalState, isOpen: false, })} />
		</Restricted>
	);
}
